"use client";

import Link from "next/link";
import { useEffect } from "react";
import { Star, ShoppingCart, Heart } from "lucide-react";
import styles from "styles/components/books-section.module.css";
import base from "lib/base";
import { useCartStore } from "context/cartStore";
import { useAuthUser } from "hooks/useAuth";
import { useWishStore } from "context/wishStore";

const resolveImage = (path) => {
  if (!path) return "/assets/img/placeholder.png"; // Fallback
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = String(path).replace(/^\/+/, "");
  const cdnRoot = String(base.cdnUrl || "").replace(/\/+$/, "");
  return `${cdnRoot}/450/${normalized}`;
};

export const BookCard = ({ book }) => {
  const { addToCart } = useCartStore();
  const user = useAuthUser();
  const { toggleWishlist } = useWishStore();
  const wishItems = useWishStore((state) => state.items);

  // Parsing props safely
  const image = resolveImage(
    book?.pictures?.[0] || book?.images?.[0] || book?.image,
  );
  const title = book?.name || "Untitled";
  const author = book?.author?.name || book?.author || "Unknown";

  const deliveryPrice = book?.price?.delivery?.price || 0;
  const discount = book?.price?.delivery?.discount || 0;
  const finalPrice = Math.max(0, deliveryPrice - discount);
  const slug = book?.slug || "";
  const bookHref = slug ? `/books/${slug}` : "/books";

  const ratingAvg =
    typeof book?.ratingAvg === "number"
      ? book.ratingAvg
      : typeof book?.rating?.avg === "number"
        ? book.rating.avg
        : typeof book?.rating === "number"
          ? book.rating
          : 0;

  const rating = Math.round(ratingAvg || 0);

  const handleAddToCart = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await addToCart(book, 1, user);
  };

  const handleToggleWish = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await toggleWishlist(book, user);
  };

  const wished = !!wishItems?.some(
    (i) => (i?.book?._id || i?._id) === book?._id,
  );

  return (
    <div className={styles.card}>
      <div className={styles.imageWrapper}>
        <Link href={bookHref} className={styles.link}>
          <img
            src={image}
            alt={title}
            className={styles.image}
            width={200}
            height={300}
            loading="lazy"
            decoding="async"
          />
        </Link>
      </div>
      <div className={styles.info}>
        <h3 className={styles.bookTitle}>
          <Link href={bookHref} className={styles.link}>
            {title}
          </Link>
        </h3>
        <span className={styles.author}>{author}</span>
        <div className={styles.rating}>
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={14}
              fill={i < rating ? "#ff9800" : "none"}
              stroke={i < rating ? "#ff9800" : "#ccc"}
            />
          ))}
        </div>
        <div className={styles.price}>
          {finalPrice.toLocaleString()}₮
          {discount > 0 && (
            <span className={styles.oldPrice}>
              {deliveryPrice.toLocaleString()}₮
            </span>
          )}
        </div>
        <div className={styles.actions}>
          <button
            className={styles.cartBtn}
            aria-label="Add to cart"
            onClick={handleAddToCart}
          >
            <ShoppingCart size={16} />
          </button>
          <button
            className={`${styles.wishBtn} ${wished ? styles.wishBtnActive : ""}`}
            aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
            onClick={handleToggleWish}
          >
            <Heart size={16} fill={wished ? "#e53935" : "none"} />
          </button>
        </div>
      </div>
    </div>
  );
};

const BooksSection = ({ books = [], title = "Шинэ номнууд" }) => {
  const user = useAuthUser();
  const { fetchWishlist } = useWishStore();

  useEffect(() => {
    if (user) fetchWishlist(user);
  }, [user, fetchWishlist]);

  if (!books || books.length === 0) {
    return null;
  }

  return (
    <section className={`section ${styles.section}`}>
      <div className="container">
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
        </div>
        <div className={`row ${styles.booksRow}`}>
          {books.map((book) => (
            <div className="col-12 col-md-6 col-lg-4" key={book._id || book.id}>
              <BookCard book={book} />
            </div>
          ))}
        </div>
        <div className={styles.footer}>
          <Link href="/books" className={styles.allBtn}>
            Бүх номнууд
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BooksSection;
